/**
 * Tab Stickies Store
 *
 * Zustand store for managing per-tab sticky notes.
 * Each tab (e.g., /app/epg, /app/notes) has its own sticky notes.
 *
 * Key differences from useUserStickiesStore:
 * - Tab-scoped: stickies are organized by tab_path
 * - Uses dedicated API endpoints (/api/tab-stickies)
 * - Each tab loads independently with its own loading state
 */

import { create } from 'zustand';
import type { TabStickyNote, StickyColor } from '@shared/types/tabStickies';
import { stickyColorOrder, TAB_STICKY_DEFAULTS, TAB_STICKY_POSITION_OFFSET } from '@shared/types/tabStickies';

/**
 * State for each tab's stickies
 */
interface TabState {
  stickies: TabStickyNote[];
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;
}

/**
 * Store state and actions
 */
interface TabStickiesState {
  // Data organized by tab path
  tabs: Record<string, TabState>;

  // Delete confirmation modal state
  deleteModalOpen: boolean;
  stickyToDelete: { id: string; tabPath: string } | null;

  // Tab state getters/setters
  getTabState: (tabPath: string) => TabState;
  setTabLoading: (tabPath: string, loading: boolean) => void;
  setTabError: (tabPath: string, error: string | null) => void;
  setTabInitialized: (tabPath: string, initialized: boolean) => void;

  // CRUD Actions
  hydrateTab: (tabPath: string, stickies: TabStickyNote[]) => void;
  addStickyOptimistic: (tabPath: string, sticky: TabStickyNote) => void;
  updateStickyOptimistic: (tabPath: string, id: string, updates: Partial<TabStickyNote>) => void;
  deleteStickyOptimistic: (tabPath: string, id: string) => void;

  // Convenience actions for local state updates
  setContent: (tabPath: string, id: string, content: string) => void;
  setTitle: (tabPath: string, id: string, title: string) => void;
  setPosition: (tabPath: string, id: string, x: number, y: number) => void;
  setSize: (tabPath: string, id: string, width: number, height: number) => void;
  setCollapsed: (tabPath: string, id: string, collapsed: boolean) => void;
  toggleCollapsed: (tabPath: string, id: string) => void;
  setZIndex: (tabPath: string, id: string, zIndex: number) => void;
  bringToFront: (tabPath: string, id: string) => void;

  // Delete Modal Actions
  openDeleteModal: (id: string, tabPath: string) => void;
  closeDeleteModal: () => void;

  // Reset
  reset: () => void;
  resetTab: (tabPath: string) => void;
}

/**
 * Default tab state
 */
const defaultTabState: TabState = {
  stickies: [],
  isLoading: false,
  isInitialized: false,
  error: null,
};

/**
 * Initial store state
 */
const initialState = {
  tabs: {},
  deleteModalOpen: false,
  stickyToDelete: null,
};

/**
 * Get next color based on existing stickies count
 */
export const getNextColor = (stickies: TabStickyNote[]): StickyColor => {
  return stickyColorOrder[stickies.length % stickyColorOrder.length];
};

/**
 * Get position for new sticky (staggered from existing ones)
 */
export const getNextPosition = (stickies: TabStickyNote[]): { x: number; y: number } => {
  const offset = stickies.length * TAB_STICKY_POSITION_OFFSET;
  return {
    x: TAB_STICKY_DEFAULTS.POSITION_X + offset,
    y: TAB_STICKY_DEFAULTS.POSITION_Y + offset,
  };
};

/**
 * Get next z-index (one above the highest)
 */
export const getNextZIndex = (stickies: TabStickyNote[]): number => {
  if (stickies.length === 0) return 0;
  const maxZ = Math.max(...stickies.map((s) => s.z_index));
  return maxZ + 1;
};

/**
 * Tab Stickies Zustand Store
 */
export const useTabStickiesStore = create<TabStickiesState>((set, get) => ({
  ...initialState,

  // Get or create tab state
  getTabState: (tabPath: string) => {
    const { tabs } = get();
    return tabs[tabPath] || defaultTabState;
  },

  // Tab state setters
  setTabLoading: (tabPath, loading) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabPath]: {
          ...(state.tabs[tabPath] || defaultTabState),
          isLoading: loading,
        },
      },
    }));
  },

  setTabError: (tabPath, error) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabPath]: {
          ...(state.tabs[tabPath] || defaultTabState),
          error,
        },
      },
    }));
  },

  setTabInitialized: (tabPath, initialized) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabPath]: {
          ...(state.tabs[tabPath] || defaultTabState),
          isInitialized: initialized,
        },
      },
    }));
  },

  // Hydrate a tab with fetched stickies
  hydrateTab: (tabPath, stickies) => {
    set((state) => ({
      tabs: {
        ...state.tabs,
        [tabPath]: {
          stickies,
          isLoading: false,
          isInitialized: true,
          error: null,
        },
      },
    }));
  },

  // Optimistic add
  addStickyOptimistic: (tabPath, sticky) => {
    set((state) => {
      const tabState = state.tabs[tabPath] || defaultTabState;
      return {
        tabs: {
          ...state.tabs,
          [tabPath]: {
            ...tabState,
            stickies: [...tabState.stickies, sticky],
          },
        },
      };
    });
  },

  // Optimistic update
  updateStickyOptimistic: (tabPath, id, updates) => {
    set((state) => {
      const tabState = state.tabs[tabPath] || defaultTabState;
      return {
        tabs: {
          ...state.tabs,
          [tabPath]: {
            ...tabState,
            stickies: tabState.stickies.map((sticky) =>
              sticky.id === id ? { ...sticky, ...updates } : sticky
            ),
          },
        },
      };
    });
  },

  // Optimistic delete
  deleteStickyOptimistic: (tabPath, id) => {
    set((state) => {
      const tabState = state.tabs[tabPath] || defaultTabState;
      return {
        tabs: {
          ...state.tabs,
          [tabPath]: {
            ...tabState,
            stickies: tabState.stickies.filter((sticky) => sticky.id !== id),
          },
        },
      };
    });
  },

  // Convenience: update content
  setContent: (tabPath, id, content) => {
    get().updateStickyOptimistic(tabPath, id, { content });
  },

  // Convenience: update title
  setTitle: (tabPath, id, title) => {
    get().updateStickyOptimistic(tabPath, id, { title });
  },

  // Convenience: update position
  setPosition: (tabPath, id, x, y) => {
    get().updateStickyOptimistic(tabPath, id, { position_x: x, position_y: y });
  },

  // Convenience: update size
  setSize: (tabPath, id, width, height) => {
    get().updateStickyOptimistic(tabPath, id, { width, height });
  },

  // Convenience: set collapsed
  setCollapsed: (tabPath, id, collapsed) => {
    get().updateStickyOptimistic(tabPath, id, { is_collapsed: collapsed });
  },

  // Convenience: toggle collapsed
  toggleCollapsed: (tabPath, id) => {
    const tabState = get().getTabState(tabPath);
    const sticky = tabState.stickies.find((s) => s.id === id);
    if (sticky) {
      get().updateStickyOptimistic(tabPath, id, { is_collapsed: !sticky.is_collapsed });
    }
  },

  // Convenience: set z-index
  setZIndex: (tabPath, id, zIndex) => {
    get().updateStickyOptimistic(tabPath, id, { z_index: zIndex });
  },

  // Convenience: bring to front (highest z-index)
  bringToFront: (tabPath, id) => {
    const tabState = get().getTabState(tabPath);
    const nextZ = getNextZIndex(tabState.stickies);
    get().updateStickyOptimistic(tabPath, id, { z_index: nextZ });
  },

  // Delete Modal Actions
  openDeleteModal: (id, tabPath) => {
    set({ deleteModalOpen: true, stickyToDelete: { id, tabPath } });
  },

  closeDeleteModal: () => {
    set({ deleteModalOpen: false, stickyToDelete: null });
  },

  // Reset all state
  reset: () => set(initialState),

  // Reset a specific tab
  resetTab: (tabPath) => {
    set((state) => {
      const { [tabPath]: _removed, ...rest } = state.tabs;
      return { tabs: rest };
    });
  },
}));
